/**
 * Contenedor de la aplicación y máquina de estados del arranque.
 *
 * Hay cuatro estados posibles al abrir y los cuatro son legítimos:
 *
 *   1. Comprobando si hay sesión guardada  -> pantalla de carga
 *   2. Sin backend configurado             -> modo local, se juega igual
 *   3. Con backend y sin sesión            -> pantalla de acceso
 *   4. Con sesión                          -> se carga perfil, colección y mazo
 *
 * El estado 2 es el que suele olvidarse y el que más se agradece: sin él, nadie
 * puede abrir el proyecto ni probar el tablero sin crearse antes un proyecto de
 * Supabase.
 *
 * El marco visual no cambia entre los cuatro: alto exacto de viewport, sin
 * scroll de página y con el área segura del dispositivo respetada. Un juego de
 * mesa se rompe si la página hace scroll — arrastrar una carta hacia arriba
 * movería la página en lugar de la carta. Y `100dvh` en vez de `100vh` porque en
 * los navegadores de móvil la barra de direcciones se retrae al hacer scroll, y
 * con `vh` la mano se queda medio tapada.
 */

import { CloudOff, Loader2 } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { GameBoard } from '@/components/board/GameBoard';
import { PvpPanel } from '@/components/board/PvpPanel';
import { CollectionScreen } from '@/components/collection/CollectionScreen';
import { MarketScreen } from '@/components/market/MarketScreen';
import { ShopScreen } from '@/components/shop/ShopScreen';
import { AuthPanel } from '@/components/ui/AuthPanel';
import { Navbar, type SectionId } from '@/components/ui/Navbar';
import { useAuth } from '@/hooks/useAuth';
import { usePlayerData } from '@/hooks/useUserInventory';
import { setupNativeShell } from '@/services/nativeShell';

/**
 * Saldos del modo local. No se guardan en ninguna parte y no compran nada: son
 * de relleno para que la cabecera no salga vacía mientras no haya backend.
 */
const LOCAL_WALLET = { gold: 500, gems: 50, isVip: false };

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-linear-to-b from-slate-900 via-slate-950 to-black text-slate-100">
      {children}
    </div>
  );
}

function Splash({ message }: { message: string }) {
  return (
    <Shell>
      <div className="grid flex-1 place-items-center gap-3 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
        <p className="text-sm">{message}</p>
      </div>
    </Shell>
  );
}

export default function App() {
  const auth = useAuth();
  // La sección vive en estado y no en la URL: la aplicación se empaqueta con
  // Capacitor, donde no hay barra de direcciones que aproveche una ruta. Cuando
  // haga falta compartir enlaces a una carta concreta, esto pasa a un router.
  const [section, setSection] = useState<SectionId>('play');

  // Envoltorio nativo. En el navegador no hace nada; en la app instalada oculta
  // la pantalla de carga y captura el botón atrás de Android, que por defecto
  // cerraría la aplicación en mitad de una partida.
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    void setupNativeShell(() => {
      // Desde cualquier sección se vuelve a la partida; solo desde la partida se
      // deja salir de la app.
      if (section !== 'play') {
        setSection('play');
        return true;
      }
      return false;
    }).then((dispose) => {
      cleanup = dispose;
    });
    return () => cleanup?.();
  }, [section]);
  // Los hooks van antes de cualquier return: con `enabled` desactivado en modo
  // local no llegan a pedir nada, pero React exige que se llamen siempre.
  const player = usePlayerData(auth.user?.id);

  if (auth.isLoading) {
    return <Splash message="Comprobando sesión…" />;
  }

  if (!auth.isOffline && !auth.session) {
    return (
      <Shell>
        <AuthPanel
          isBusy={auth.isLoading}
          error={auth.error}
          onAnonymous={() => void auth.signInAnonymously()}
          onSignIn={(email, password) => void auth.signInWithEmail(email, password)}
          onSignUp={(email, password, username) =>
            void auth.signUpWithEmail(email, password, username || undefined)
          }
        />
      </Shell>
    );
  }

  // Se espera a tener el mazo antes de repartir. Si se empezara la partida y el
  // mazo llegara después, se estaría jugando con el mazo inicial y no con el
  // del jugador, sin que nada lo indicara.
  if (!auth.isOffline && player.isLoading) {
    return <Splash message="Cargando tu colección…" />;
  }

  const wallet = player.profile ?? LOCAL_WALLET;

  return (
    <Shell>
      <Navbar
        active={section}
        gold={wallet.gold}
        gems={wallet.gems}
        isVip={wallet.isVip}
        username={player.profile?.username}
        onNavigate={setSection}
      />

      {auth.isOffline ? (
        <p className="flex shrink-0 items-center justify-center gap-1.5 bg-slate-800/70 px-3 py-1 text-[11px] text-slate-400">
          <CloudOff className="h-3 w-3" aria-hidden />
          Modo local: sin cuenta ni colección. Configura <code>.env.local</code> para activarlas.
        </p>
      ) : null}

      {player.isDeckIncomplete ? (
        <p className="shrink-0 bg-amber-900/40 px-3 py-1 text-center text-[11px] text-amber-200">
          A tu mazo activo le faltan cartas. Revísalo en la colección.
        </p>
      ) : null}

      <main className="flex min-h-0 flex-1 flex-col">
        {section === 'collection' ? (
          <CollectionScreen
            cards={player.inventory}
            profile={player.profile}
            deck={player.deck}
            userId={auth.user?.id}
            isLoading={player.isLoading}
            isOffline={auth.isOffline}
          />
        ) : section === 'shop' ? (
          <ShopScreen profile={player.profile} userId={auth.user?.id} isOffline={auth.isOffline} />
        ) : section === 'market' ? (
          <MarketScreen
            profile={player.profile}
            inventory={player.inventory}
            deck={player.deck}
            userId={auth.user?.id}
            isOffline={auth.isOffline}
          />
        ) : (
          // El tablero se mantiene montado al cambiar de sección: desmontarlo
          // perdería la partida en curso, y volver de la colección para
          // encontrarse el turno 1 sería insufrible.
          <div className={section === 'play' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}>
            <GameBoard deckMasterIds={player.deckMasterIds} />
            <PvpPanel
              userId={auth.user?.id}
              deckMasterIds={player.deckMasterIds}
              isOffline={auth.isOffline}
            />
          </div>
        )}
      </main>
    </Shell>
  );
}
